import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EnrollmentType } from '../../../../../domain/enums/enrollment-type.enum';
import { TaskStatusEnum } from '../../../../../domain/enums/task-status.enum';
import { TaskModel } from '../../../../../domain/models/task.model';
import { EnrollmentModel } from '../../../../../domain/models/enrollment.model';
import { AccountModel } from '../../../../../domain/models/account.model';
import { AccountRentalPropertyModel } from '../../../../../domain/models/account-rental-property.model';
import { TaskService } from '../../../../../domain/services/task/task.service';
import { EnrollmentService } from '../../../../../domain/services/enrollment/enrollment.service';
import { AccountService } from '../../../../../domain/services/account/account.service';
import { PropertyService } from '../../../../../domain/services/property/property.service';
import { DialogService } from '../../../../../domain/services/dialog/dialog.service';
import { Router } from '@angular/router';
import { EnrollmentStatus } from '../../../../../domain/enums/enrollment-status.enum';
import { EnrollmentComponent } from '../../../../../common/enrollment-request/EnrollmentComponent';
import { buildValidationErrorMessage } from '../../../../../utils/invalid-fields.util';
import {
  IndividualOwnerEnrollmentComponent
} from '../../../../../common/enrollment-request/individual-owner-enrollment/individual-owner-enrollment.component';
import {
  PropertyManagementEnrollmentComponent
} from '../../../../../common/enrollment-request/property-management-enrollment/property-management-enrollment.component';
import {
  IndividualBedEnrollmentComponent
} from '../../../../../common/enrollment-request/individual-bed-enrollment/individual-bed-enrollment.component';
import {
  DefaultEnrollmentComponent
} from '../../../../../common/enrollment-request/default-enrollment/default-enrollment.component';
import {
  CustomerResumeBarComponent
} from '../../../../../common/customer-resume-bar/customer-resume-bar.component';
import {
  ApproveDialogComponent
} from '../../../../../common/enrollment-request/approve-dialog/approve-dialog.component';
import {
  DeclineDialogComponent
} from '../../../../../common/enrollment-request/decline-dialog/decline-dialog.component';
import { TaskActionBarComponent } from '../task-action-bar/task-action-bar.component';
import { UnderConstructionComponent } from '../../../../../common/under-construction/under-construction.component';
import {
  EnrollmentRentalPropertyComponent
} from '../../../../../common/enrollment-rental-property/enrollment-rental-property.component';
import { PropertyOwnerModel } from '../../../../../domain/models/property-owner.model';
import { PropertyMatchCardComponent } from '../../../../../common/property-match-card/property-match-card.component';
import { PropertyShareService } from '../../../../../domain/services/property-share/property-share.service';
import { LinkResult, PropertyMatch } from '../../../../../domain/models/property-share.model';

@Component({
  selector: 'app-new-property-task',
  standalone: true,
  imports: [
    CommonModule,
    MatButton,
    MatIcon,
    CustomerResumeBarComponent,
    IndividualOwnerEnrollmentComponent,
    PropertyManagementEnrollmentComponent,
    IndividualBedEnrollmentComponent,
    DefaultEnrollmentComponent,
    TaskActionBarComponent,
    UnderConstructionComponent,
    EnrollmentRentalPropertyComponent,
    PropertyMatchCardComponent,
  ],
  templateUrl: './new-property-task.component.html',
  styleUrl: './new-property-task.component.css',
})
export class NewPropertyTaskComponent implements OnInit {
  @Input() currentTask!: TaskModel;
  @ViewChild(IndividualOwnerEnrollmentComponent) individualOwnerEnrollmentComponent!: IndividualOwnerEnrollmentComponent;
  @ViewChild(PropertyManagementEnrollmentComponent) propertyManagementEnrollmentComponent!: PropertyManagementEnrollmentComponent;
  @ViewChild(IndividualBedEnrollmentComponent) individualBedEnrollmentComponent!: IndividualBedEnrollmentComponent;
  @ViewChild(DefaultEnrollmentComponent) defaultEnrollmentComponent!: DefaultEnrollmentComponent;
  @ViewChild(TaskActionBarComponent) taskActionBar!: TaskActionBarComponent;
  @ViewChild(EnrollmentRentalPropertyComponent) propertyDetailComponent!: EnrollmentRentalPropertyComponent;

  currentEnrollment!: EnrollmentModel;
  currentAccount: AccountModel | undefined;
  // Set for "add property to existing account" tasks (no enrollment); the property
  // under review is loaded from the task's relatedEntityId.
  currentProperty: AccountRentalPropertyModel | undefined;
  propertyOwners: PropertyOwnerModel[] = [];
  recentlyLocked = false;
  disableSubmit = false;
  protected readonly EnrollmentType = EnrollmentType;
  protected readonly TaskStatusEnum = TaskStatusEnum;

  /** Same-address properties on other accounts (add-property tasks only). Linking is optional. */
  unresolvedMatches: PropertyMatch[] = [];
  linkResult: LinkResult | null = null;
  linkedAccountName = '';
  matchBusy = false;

  constructor(
    private readonly propertyShareService: PropertyShareService,
    private readonly taskService: TaskService,
    private readonly enrollmentService: EnrollmentService,
    private readonly accountService: AccountService,
    private readonly propertyService: PropertyService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    if (this.currentTask.enrollmentRequestId) {
      // Enrollment-based new property: load the enrollment (and its account).
      this.fetchEnrollment(this.currentTask.enrollmentRequestId);
      if (this.currentTask.relatedEntityId) {
        this.fetchAccount(this.currentTask.relatedEntityId);
      }
    } else if (this.currentTask.relatedEntityId) {
      // Add property to an existing account: relatedEntityId is the rental property id.
      this.fetchProperty(this.currentTask.relatedEntityId);
      this.fetchMatches();
    }
  }

  private fetchMatches(): void {
    this.propertyShareService.propertyMatches(this.currentTask.relatedEntityId).subscribe({
      next: matches => this.unresolvedMatches = matches ?? [],
      error: () => this.unresolvedMatches = [],
    });
  }

  /** Undo a link made on this screen (also the way out of the decline 409). */
  unlinkResult(): void {
    if (!this.linkResult) return;
    this.dialogService.openConfirmationDialog('Unlink property',
      `Unlink this property from ${this.linkedAccountName}? Each account files its own returns again.`, (ok: boolean) => {
        if (ok !== true || !this.linkResult) return;
        this.matchBusy = true;
        this.propertyShareService.unlink(this.linkResult.shareId).subscribe({
          next: () => {
            this.matchBusy = false;
            this.linkResult = null;
            this.fetchMatches();
          },
          error: err => {
            this.matchBusy = false;
            this.dialogService.openErrorDialog('Unable to unlink', err?.error?.message ?? 'Please try again.');
          },
        });
      });
  }

  get newIsManagementCompany(): boolean {
    return this.currentAccount?.type === 'PROPERTY_MANAGEMENT';
  }

  /** Link now: guest = the property under review unless it is the home. */
  onLink(match: PropertyMatch, newIsHome: boolean): void {
    const self = this.currentTask.relatedEntityId;
    const [guest, home] = newIsHome ? [match.propertyId, self] : [self, match.propertyId];
    this.matchBusy = true;
    this.propertyShareService.link(guest, home).subscribe({
      next: result => {
        this.matchBusy = false;
        this.linkResult = result;
        this.linkedAccountName = match.accountName;
        this.unresolvedMatches = [];
      },
      error: err => {
        this.matchBusy = false;
        this.dialogService.openErrorDialog('Unable to link', err?.error?.message ?? 'Please try again.');
      },
    });
  }

  onNotSame(match: PropertyMatch): void {
    this.matchBusy = true;
    this.propertyShareService.dismiss(this.currentTask.relatedEntityId, match.propertyId).subscribe({
      next: () => {
        this.matchBusy = false;
        this.unresolvedMatches = this.unresolvedMatches.filter(m => m !== match);
      },
      error: err => {
        this.matchBusy = false;
        this.dialogService.openErrorDialog('Unable to dismiss', err?.error?.message ?? 'Please try again.');
      },
    });
  }

  /** True for "add property to existing account" tasks, which have no enrollment. */
  get isPropertyOnlyTask(): boolean {
    return !this.currentTask.enrollmentRequestId && !!this.currentProperty;
  }

  getEnrollmentComponent(): EnrollmentComponent | undefined {
    if (this.currentEnrollment?.enrollmentType) {
      switch (this.currentEnrollment.enrollmentType) {
        case EnrollmentType.INDIVIDUAL_OWNER:
          return this.individualOwnerEnrollmentComponent;
        case EnrollmentType.PROPERTY_MANAGEMENT:
          return this.propertyManagementEnrollmentComponent;
        case EnrollmentType.INDIVIDUAL_BED:
          return this.individualBedEnrollmentComponent;
        default:
          return this.defaultEnrollmentComponent;
      }
    }
    return undefined;
  }

  isTaskApproved(approved: boolean): void {
    if (approved) {
      this.approve();
    } else {
      this.decline();
    }
  }

  private fetchEnrollment(id: number): void {
    this.enrollmentService.getEnrollmentById(id).subscribe(value => {
      this.currentEnrollment = value;
    });
  }

  private fetchAccount(id: number): void {
    this.accountService.getById(id).subscribe(account => {
      this.currentAccount = account;
    });
  }

  private fetchProperty(id: number): void {
    this.propertyService.getById(id).subscribe(property => {
      this.currentProperty = property;
      // Load the owning account for the customer resume bar.
      if (property?.accountID) {
        this.fetchAccount(property.accountID);
      }
    });
    this.propertyService.getOwnersByPropertyId(id).subscribe(owners => {
      this.propertyOwners = owners ?? [];
    });
  }

  private approve(): void {
    if (this.unresolvedMatches.length) {
      this.dialogService.openConfirmationDialog('Possible duplicate',
        'Approve without resolving the possible duplicate?', (ok: boolean) => {
          if (ok === true) {
            this.confirmApprove();
          } else {
            this.taskActionBar.resetLoading();
          }
        });
      return;
    }
    this.confirmApprove();
  }

  private confirmApprove(): void {
    this.dialogService.openCustomDialog(ApproveDialogComponent, (value: boolean) => {
      if (value) {
        this.submitApprove();
      } else {
        this.taskActionBar.resetLoading();
      }
    });
  }

  private decline(): void {
    this.dialogService.openCustomDialog(DeclineDialogComponent, (value: boolean) => {
      if (value) {
        this.submitDecline();
      } else {
        this.taskActionBar.resetLoading();
      }
    });
  }

  private submitApprove(): void {
    // Add-property task: validate the property detail (appraiser office ID is required),
    // persist the reviewed property, then approve — which activates it server-side.
    if (this.isPropertyOnlyTask) {
      if (!this.propertyDetailComponent?.isFormValid()) {
        this.taskActionBar.resetLoading();
        this.propertyDetailComponent?.raiseFormValidations();
        setTimeout(() => {
          this.dialogService.openErrorDialog('Unable to submit', buildValidationErrorMessage());
        });
        return;
      }
      this.disableSubmit = true;
      const edited = this.propertyDetailComponent.getUpdatedEnrollment().enrollmentRentalProperty;
      const updatedProperty: AccountRentalPropertyModel = {
        ...this.currentProperty,
        ...edited,
        id: this.currentProperty?.id,
        accountID: this.currentProperty?.accountID,
      };
      this.propertyService.updateProperty(updatedProperty).subscribe({
        next: () => {
          this.taskService.approve(this.currentTask).subscribe({
            next: () => {
              this.currentTask.status = TaskStatusEnum.APPROVED;
              this.exit();
            },
            error: (error) => {
              this.dialogService.openErrorDialog('Unable to submit', error?.error?.message || error?.error?.error || 'Approval failed.');
              this.disableSubmit = false;
              this.taskActionBar.resetLoading();
            },
          });
        },
        error: (error) => {
          this.dialogService.openErrorDialog('Unable to submit', error?.error?.message || error?.error?.error || 'Could not save the property.');
          this.disableSubmit = false;
          this.taskActionBar.resetLoading();
        },
      });
      return;
    }

    const enrollmentComponent = this.getEnrollmentComponent();
    if (enrollmentComponent?.isFormValid()) {
      this.disableSubmit = true;
      const updated = { ...enrollmentComponent.getUpdatedEnrollment(), status: EnrollmentStatus.Approved };
      this.enrollmentService.approveEnrollment(updated).subscribe(
        () => {
          this.recentlyLocked = false;
          this.currentTask.status = TaskStatusEnum.APPROVED;
          this.exit();
        },
        (error) => {
          this.dialogService.openErrorDialog('Unable to submit', error.error.error);
          this.disableSubmit = false;
          this.taskActionBar.resetLoading();
        },
      );
    } else {
      this.taskActionBar.resetLoading();
      enrollmentComponent?.raiseFormValidations();
      setTimeout(() => {
        const msg = buildValidationErrorMessage();
        this.dialogService.openErrorDialog('Unable to submit', msg);
      });
    }
  }

  saving = false;

  /** Persist review edits without approving (QA 2026-09-30). Same save the approve path does first. */
  saveEdits(): void {
    const done = {
      next: () => {
        this.saving = false;
        this.dialogService.openSuccesDialog('Saved', 'Your changes were saved. The task is still open.');
      },
      error: (error: any) => {
        this.saving = false;
        this.dialogService.openErrorDialog('Unable to save', error?.error?.message ?? error?.error?.error ?? 'Please try again.');
      },
    };
    if (this.isPropertyOnlyTask) {
      if (!this.propertyDetailComponent) return;
      const edited = this.propertyDetailComponent.getUpdatedEnrollment().enrollmentRentalProperty;
      this.saving = true;
      this.propertyService.updateProperty({
        ...this.currentProperty, ...edited, id: this.currentProperty?.id, accountID: this.currentProperty?.accountID,
      } as AccountRentalPropertyModel).subscribe(done);
      return;
    }
    const enrollmentComponent = this.getEnrollmentComponent();
    if (!enrollmentComponent) return;
    this.saving = true;
    this.enrollmentService.saveForReview(enrollmentComponent.getUpdatedEnrollment()).subscribe(done);
  }

  private submitDecline(): void {
    // Add-property task: just decline the task (server deactivates the property).
    // A linked property cannot be declined (409 "...unlink it first"): show it and stop the spinner.
    const onError = (err: any) => {
      this.dialogService.openErrorDialog('Unable to decline', err?.error?.message || err?.error?.error || 'Please try again.');
      this.taskActionBar.resetLoading();
    };
    if (this.isPropertyOnlyTask) {
      this.taskService.decline(this.currentTask).subscribe({
        next: () => {
          this.recentlyLocked = false;
          this.exit();
        },
        error: onError,
      });
      return;
    }

    this.taskService.decline(this.currentTask).subscribe({
      next: () => {
        this.recentlyLocked = false;
        const updated = { ...this.currentEnrollment, status: EnrollmentStatus.Declined };
        this.enrollmentService.updateEnrollment(updated).subscribe({
          next: () => {
            this.recentlyLocked = false;
            this.exit();
          },
          error: onError,
        });
      },
      error: onError,
    });
  }

  private exit(): void {
    this.router.navigate(['/app/tasks']);
  }
}
